import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { useNotificationContext } from "contexts/NotificationContext";
import { useNavigate, useParams } from "react-router-dom";
import { RouteName } from "constants/route";
import { ILoadingData, ILoadingWithPage } from "models";
import { IFleetUpdateInput } from "models/fleet";
import { updateFleeValidator } from "validators/createFlee.validator";
import { fetchFleetByIdApi, updateFleetApi } from "api/fleet";
import { IFleetModel } from "models/fleetModel";
import { activeFleetModelsApi } from "api/fleetModel";
import { RcFile } from "antd/es/upload";
import { uploadFleetApi, uploadLicenseApi } from "api/upload";

export default function useEditFleet() {
  const id: string = useParams().id!;
  const navigate = useNavigate();
  const { showError, showSuccess } = useNotificationContext();
  const [loading, setLoading] = useState<ILoadingWithPage>({
    button: false,
    page: true,
  });
  const [fleetModels, setFleetModels] = useState<ILoadingData<IFleetModel>>({
    data: [],
    loading: true,
  });

  const { control, handleSubmit, setValue } = useForm<IFleetUpdateInput>({
    resolver: yupResolver(updateFleeValidator),
  });

  useEffect(() => {
    fetchFleetByIdApi(id).then(({ data, error }) => {
      if (data?.data) {
        setValue("brand", data.data.brand);
        setValue("model", data.data.model);
        setValue("color", data.data.color);
        setValue("fleet_model_id", data.data.fleet_model_id);
        setValue("plate_number", data.data.plate_number);
        setValue("license_number", data.data.license_number);
        setValue("brand_number", data.data.brand_number);
        setValue("category", data.data.category);
        setValue("production_year", data.data.production_year.toString());
        setValue("has_tinted_windows", data.data.has_tinted_windows);
        setValue("has_ac", data.data.has_ac);
        setValue("door_number", data.data.door_number.toString());
        setValue("passenger_seat", data.data.passenger_seat.toString());
        setValue("image", data.data.image ? [{ url: data.data.image }] : []);
        setValue(
          "license_image",
          data.data.license_image ? [{ url: data.data.license_image }] : []
        );
      } else showError(error!.message);
      activeFleetModelsApi().then(({ data, error }) => {
        setLoading({ button: false, page: false });
        if (data) {
          setFleetModels({ data: data.data, loading: false });
        } else showError(error!.message);
      });
    });
    // eslint-disable-next-line
  }, [id]);

  const onSubmit = async (input: IFleetUpdateInput) => {
    setLoading({ button: true, page: false });
    
    // Helper to extract URL from various formats
    const extractUrl = (val: any) => {
      if (Array.isArray(val) && val.length > 0) {
        const file = val[0];
        return file.url || file.response?.data?.url || file.response?.url || null;
      }
      return null;
    };

    input.image = extractUrl(input.image);
    input.license_image = extractUrl(input.license_image);

    const { data, error } = await updateFleetApi(id, input);
    if (data) {
      showSuccess("Fleet updated Successfully");
      navigate(RouteName.FLEET, { replace: true });
    } else {
      showError(error!.message);
    }
    setLoading({ button: false, page: false });
  };

  const handleUploadFleetImage = async (file: RcFile) => {
    const { data, error } = await uploadFleetApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };

  const handleUploadLicenseImage = async (file: RcFile) => {
    const { data, error } = await uploadLicenseApi(file);
    if (data && data.data) {
      return data.data.url;
    } else showError(`Error while uploading image: ${error?.message}`);
  };

  return {
    handleSubmit,
    onSubmit,
    handleUploadFleetImage,
    handleUploadLicenseImage,
    control,
    loading,
    fleetModels,
  };
}
